import {
  Button,
  Grid,
  Stack,
  FormControl,
  OutlinedInput,
  InputAdornment,
  Typography,
  Modal,
  IconButton,
  Box
} from '@mui/material';

import { DataGrid } from '@mui/x-data-grid';
import { IconPlus } from '@tabler/icons';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import MainCard from 'ui-component/cards/MainCard';
import { get, del } from '../../../axios/apiService';
import LoadingBackdrop from 'ui-component/full-screen-loadder/LoadingBackdrop';
import CustomNoRowsOverlay from 'ui-component/customNoRowsOverlay';
import toast from 'react-hot-toast';
import { usePermissions } from 'hooks/usePermissions';
import { MODULE_KEYS } from 'store/constant';
import { Visibility } from '@mui/icons-material';

const Campaign = () => {
  const navigate = useNavigate();

  const [isLoading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [rowCount, setRowCount] = useState(0);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  });

  const [sortModel, setSortModel] = useState([]);
  const [search, setSearch] = useState('');

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState('');
  const { hasWritePermission } = usePermissions();
  const canEdit = hasWritePermission(MODULE_KEYS.Campaign);

  // Convert MUI column sort → API field
  const getSortField = (field) => {
    switch (field) {
      case 'name':
        return 'Name';
      case 'isScheduleLater':
        return 'IsScheduleLater';
      default:
        return 'Name';
    }
  };

  // Fetch Campaigns
  useEffect(() => {
    const fetchCampaigns = async () => {
      setLoading(true);

      try {
        const sortField =
          sortModel.length > 0
            ? getSortField(sortModel[0].field)
            : 'Name';

        const sortDirection =
          sortModel.length > 0
            ? sortModel[0].sort === 'asc'
              ? 'Ascending'
              : 'Descending'
            : 'Ascending';

        const filterQuery = search
          ? `&$filter=Name~LIKE~${encodeURIComponent(search)}`
          : '';

        const url = `Campaign?$skip=${paginationModel.page * paginationModel.pageSize
          }&$top=${paginationModel.pageSize}&$orderby=${sortField}&$orderbydirection=${sortDirection}${filterQuery}`;

        const response = await get(url);

        if (response?.values) {
          const mapped = response.values.map((c) => ({
            id: c.id,
            name: c.name,
            templatename: c.templateName,
            isScheduleLater: c.isScheduleLater ? 'Scheduled' : 'Immediate'
          }));

          setCampaigns(mapped);
          setRowCount(response.totalCount || 0);
        } else {
          setCampaigns([]);
          setRowCount(0);
        }
      } catch (err) {
        console.error(err);
        toast.error('Failed to load campaigns');
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, [paginationModel, sortModel, search]);

  // Delete operation
  const handleDelete = async () => {
    if (!deleteId) return;

    setLoading(true);
    try {
      await del(`Campaign/${deleteId}`);
      toast.success('Campaign deleted successfully');

      // Reload table after delete
      setPaginationModel({ ...paginationModel, page: 0 });
    } catch (err) {
      toast.error('Failed to delete campaign');
    } finally {
      setDeleteOpen(false);
      setDeleteId('');
      setLoading(false);
    }
  };

  const ActionButtons = (row) => (
    <Stack direction="row" spacing={1}>
      <IconButton
        color="primary"
        onClick={() => navigate(`/marketing/edit-campaign/${row.id}`)}
      >{
          canEdit ? <EditIcon /> : <Visibility />
        }
      </IconButton>
      {
        canEdit && (
          <IconButton
            color="error"
            onClick={() => {
              setDeleteId(row.id);
              setDeleteOpen(true);
            }}
          >
            <DeleteIcon />
          </IconButton>
        )
      }
    </Stack>
  );

  const columns = [
    { field: 'name', headerName: 'Campaign Name', flex: 1 },
    { field: 'templatename', headerName: 'Template Name', flex: 1 },
    { field: 'isScheduleLater', headerName: 'Schedule Type', flex: 1 },
    {
      field: 'action',
      headerName: 'Actions',
      width: 140,
      sortable: false,
      align: 'center',
      renderCell: (params) => <ActionButtons {...params.row} />
    }
  ];

  return (
    <>
      <MainCard title="Campaign Management">
        <Grid container spacing={3}>
          {/* Search + Add */}
          <Grid item xs={12}>
            <Stack
              direction="row"
              justifyContent="flex-end"
              spacing={1}
              alignItems="center"
            >
              <FormControl sx={{ width: '28ch' }} size="small">
                <OutlinedInput
                  placeholder="Search campaign"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  endAdornment={
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  }
                />
              </FormControl>
              {
                canEdit && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<IconPlus />}
                    onClick={() => navigate('/marketing/add-campaign')}
                  >
                    Add
                  </Button>
                )
              }
            </Stack>
          </Grid>

          {/* Table */}
          <Grid item xs={12}>
            <DataGrid
              autoHeight
              rows={campaigns}
              columns={columns}
              rowCount={rowCount}
              getRowId={(row) => row.id}
              paginationMode="server"
              sortingMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              pageSizeOptions={[10]}
              components={{
                NoRowsOverlay: CustomNoRowsOverlay
              }}
            />
          </Grid>
        </Grid>
      </MainCard>

      {/* Delete Modal */}
      <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <Box className="modalBox">
          <Typography variant="h6">Delete Campaign</Typography>
          <Typography sx={{ mt: 2 }}>
            Are you sure you want to delete this campaign?
          </Typography>

          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={2}
            sx={{ mt: 3 }}
          >
            <Button
              variant="contained"
              color="error"
              onClick={() => setDeleteOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="contained" onClick={handleDelete}>
              Confirm
            </Button>
          </Stack>
        </Box>
      </Modal>

      <LoadingBackdrop open={isLoading} />
    </>
  );
};

export default Campaign;
